// kartenLeer.ts — Welche Karten der Akte (Reiter „Allgemein") sind leer und
// starten deshalb zugeklappt? (User 30.09.2026, Plan M2 in
// docs/superpowers/plans/2026-09-30-themen-uebersicht.md)
//
// Nur für den Bearbeiten-Modus der Anlage-Masken. Bei der Neuanlage ist jede
// Karte leer — dort bleibt alles offen, sonst sähe man nichts zum Ausfüllen.
//
// Eine Karte gilt als leer, wenn
//   1. ihre Kurzfassung (kartenKurzfassung.ts) leer ist — falls sie eine hat, und
//   2. jedes ihrer Formularfelder leer ist.
// Beides zusammen, damit eine Karte lieber einmal zu oft offen bleibt als
// einmal zu oft zu (z. B. Bank: Kontoinhaber ohne IBAN hat keine Kurzfassung).
//
// Karten, die hier nicht stehen, bleiben immer offen: „Allgemeine
// Informationen" (Name ist Pflicht), die Vollmachten (lädt ihre Einträge selbst
// vom Server, das Formular kennt sie nicht) und Karten, die ohnehin von sich
// aus zugeklappt starten (Social Media, Bemerkung, Persönlicher Assistent).

import type * as React from "react";

type Felder = Record<string, unknown>;

/**
 * Vorbelegungen der Maske, die NICHT als ausgefüllt zählen — wie
 * `AUTO_POPULATED_KEYS` in pages/NewPrivateContactPage.tsx (Land der
 * Anschrift = Deutschland, Staatsangehörigkeit = Deutsch).
 */
const VORBELEGT: Record<string, readonly string[]> = {
  country: ["deutschland", "de"],
  citizenship: ["deutsch", "deutschland", "de"],
};

/** Ist ein einzelner Formularwert leer (inkl. leerer JSON-Listen/-Objekte)? */
export function wertLeer(schluessel: string, wert: unknown): boolean {
  if (wert == null || wert === false) return true;
  if (Array.isArray(wert)) return wert.every((w) => wertLeer("", w));
  if (typeof wert === "object") return Object.values(wert as Felder).every((w) => wertLeer("", w));
  const s = String(wert).trim();
  if (!s || s === "null" || s === "false") return true;
  if (VORBELEGT[schluessel]?.includes(s.toLowerCase())) return true;
  if (/^[[{]/.test(s)) {
    try {
      return wertLeer("", JSON.parse(s));
    } catch {
      return false;
    }
  }
  return false;
}

const ANSCHRIFT = ["street", "postalcode", "postalCode", "city", "cityArea", "country", "additionalAddresses"];
const BANK = ["accountHolder", "iban", "bic", "bankname", "bankbezeichnung", "additionalBanks"];
const HERKUNFT = [
  "contactOriginSelections",
  "contactOriginOther",
  "kontaktherkunftDetails",
  "netzwerkInfo",
  "empfehlungsgeber",
  "herkunftEmpfehlungsgeber",
  "herkunftTippgeber",
];
const ZUWENDUNGEN = [
  "zuwendungen",
  "newsletter",
  "newsletterEmails",
  "einladungKennzeichen",
  "einladungPrioritaet",
  "eigenerNewsletter",
  "eigenerNewsletterName",
  "eigenenNewsletterAbonniert",
];

/** Karten der Personen-Maske (NewPrivateContactForm) → ihre Formularfelder. */
export const PRIVAT_KARTEN: Readonly<Record<string, readonly string[]>> = {
  "Wie kommt der Kontakt zustande?": HERKUNFT,
  "Anschrift (Erstwohnsitz)": ANSCHRIFT,
  Kommunikation: [
    "phonePrivate",
    "mobilePrivate",
    "emailPrivate",
    "phoneWork",
    "mobileWork",
    "emailWork",
    "weitereKommunikation",
  ],
  Beruf: [
    "occupationStatus",
    "employer",
    "employerContactId",
    "employerContactName",
    "industry",
    "position",
    "zusaetzlichSelbststaendig",
    "weitereTaetigkeit",
  ],
  "Vereine und Mitgliedschaften": ["hobbys", "vorlieben", "memberships", "vereine"],
  "Zuwendungen, Veranstaltungen & Newsletter": ZUWENDUNGEN,
  "Steuer & Sozialdaten": ["taxClass", "annualIncome", "taxIdNumber", "taxNumber", "socialSecurityNumber"],
  "Vertretung und Bevollmächtigte": ["verfuegungen", "rel_vertretung"],
  Bank: BANK,
  Dokumente: [
    "idCardNumber",
    "idCardSubtype",
    "idCardClasses",
    "idCardValidUntil",
    "idCardIssuedAt",
    "idCardAuthority",
    "additionalDocuments",
  ],
  "Personen die mit mir zusammen in einem Haushalt leben": ["rel_household"],
  Familienstand: ["familyStatus", "familyStatusOther", "weddingDateCivil", "weddingDateChurch"],
  "Beziehungen außerhalb des Haushaltes": ["rel_weitere"],
  "Verbindung zu Unternehmen": ["rel_personFirma"],
  Kundenverbindung: ["rel_kvGroupId"],
};

/**
 * Karten der Firmen-Maske (NewCompanyContactForm). Zwei Titel hängen an der
 * Rechtsform (Anschrift bzw. Sitz, Verantwortung) — die gibt die Maske mit.
 */
export function firmaKarten(titel: {
  anschrift: string;
  verantwortung: string;
}): Readonly<Record<string, readonly string[]>> {
  return {
    Kommunikation: ["phone", "mobile", "email", "fax", "weitereKommunikation"],
    "Wie kommt der Kontakt zustande?": HERKUNFT,
    [titel.anschrift]: ANSCHRIFT,
    Bank: BANK,
    Mitgliedschaften: ["memberships", "vereine"],
    "Zuwendungen, Veranstaltungen & Newsletter": ZUWENDUNGEN,
    [titel.verantwortung]: ["rel_menschen"],
    "Verbindungen zu Firmen": ["rel_firmaConn"],
    Kundenverbindung: ["rel_kvGroupId"],
  };
}

const kurzLeer = (k: React.ReactNode): boolean =>
  k == null || k === false || (typeof k === "string" && !k.trim());

/**
 * Die Titel aller leeren Karten — sie starten in der Akte zugeklappt.
 *
 * `kurzfassungen`: dieselbe Tabelle, die die Maske an `useKartenKlapp` gibt.
 */
export function leereKarten(
  felder: Felder,
  karten: Readonly<Record<string, readonly string[]>>,
  kurzfassungen: Record<string, React.ReactNode> = {},
): string[] {
  return Object.entries(karten)
    .filter(
      ([titel, schluessel]) =>
        (!(titel in kurzfassungen) || kurzLeer(kurzfassungen[titel])) &&
        schluessel.every((k) => wertLeer(k, felder[k])),
    )
    .map(([titel]) => titel);
}
